"use client";

import { useRef, useState } from "react";
import { Label } from "@/shadcn-components/ui/label";
import { ToggleGroup, ToggleGroupItem } from "@/shadcn-components/ui/toggle-group";
import { cn } from "@/lib/utils";
import IndicatorColorPicker, {
  normalizeHex,
} from "./IndicatorColorPicker";

const PRESET_COLORS = [
  "#FF9600",
  "#2196F3",
  "#26A69A",
  "#EF5350",
  "#FFA500",
  "#00C853",
  "#E040FB",
  "#9E9E9E",
];

function isPresetColor(color: string): boolean {
  const hex = normalizeHex(color);
  if (!hex) return false;
  return PRESET_COLORS.some((p) => p.toLowerCase() === hex.toLowerCase());
}

interface StyleConfigProps {
  label: string;
  color: string;
  style: "solid" | "dashed" | "dotted";
  onColorChange: (color: string) => void;
  onStyleChange: (style: "solid" | "dashed" | "dotted") => void;
}

function LinePreview({
  color,
  lineStyle,
}: {
  color: string;
  lineStyle: "solid" | "dashed" | "dotted";
}) {
  if (lineStyle === "dotted") {
    return (
      <div className="flex items-center gap-1 w-full max-w-[120px]">
        {[0, 1, 2, 3, 4].map((i) => (
          <span
            key={i}
            className="h-1 w-1 rounded-full shrink-0"
            style={{ backgroundColor: color }}
          />
        ))}
      </div>
    );
  }
  if (lineStyle === "dashed") {
    return (
      <div className="flex items-center gap-1 w-full max-w-[120px]">
        {[0, 1, 2, 3].map((i) => (
          <span
            key={i}
            className="h-0.5 w-5 rounded-full shrink-0"
            style={{ backgroundColor: color }}
          />
        ))}
      </div>
    );
  }
  return (
    <span
      className="block h-0.5 w-full max-w-[120px] rounded-full"
      style={{ backgroundColor: color }}
    />
  );
}

export default function StyleConfig({
  label,
  color,
  style: lineStyle,
  onColorChange,
  onStyleChange,
}: StyleConfigProps) {
  const [customPickerOpen, setCustomPickerOpen] = useState(false);
  const colorBeforePickerRef = useRef(color);

  const customActive = customPickerOpen || !isPresetColor(color);
  const displayColor = normalizeHex(color) ?? color;

  const openCustomPicker = () => {
    colorBeforePickerRef.current = color;
    setCustomPickerOpen(true);
  };

  const closeCustomPicker = () => setCustomPickerOpen(false);

  const handleCustomCancel = () => {
    onColorChange(colorBeforePickerRef.current);
    closeCustomPicker();
  };

  const handleCustomApply = (hex: string) => {
    onColorChange(hex);
    colorBeforePickerRef.current = hex;
    closeCustomPicker();
  };

  return (
    <div className="py-3 border-b border-border/60 last:border-0 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-sm font-medium">{label}</Label>
        <LinePreview color={color} lineStyle={lineStyle} />
      </div>

      <div className="flex flex-wrap gap-2">
        {PRESET_COLORS.map((preset) => (
          <button
            key={preset}
            type="button"
            onClick={() => {
              onColorChange(preset);
              closeCustomPicker();
            }}
            className={cn(
              "h-7 w-7 rounded-full border-2 transition-transform hover:scale-110",
              displayColor.toLowerCase() === preset.toLowerCase() &&
                !customPickerOpen
                ? "border-primary ring-2 ring-primary/30"
                : "border-border"
            )}
            style={{ backgroundColor: preset }}
            aria-label={`Color ${preset}`}
          />
        ))}
        <button
          type="button"
          onClick={() =>
            customPickerOpen ? handleCustomCancel() : openCustomPicker()
          }
          aria-expanded={customPickerOpen}
          className={cn(
            "h-7 min-w-[2.75rem] px-2 rounded-md border text-xs transition-colors flex items-center gap-1.5",
            customActive
              ? "border-primary ring-2 ring-primary/30 bg-muted/50"
              : "border-dashed border-border text-muted-foreground hover:bg-muted/50"
          )}
        >
          <span
            className="h-4 w-4 rounded-sm border border-border/80 shrink-0"
            style={{
              backgroundColor: customActive ? displayColor : undefined,
              backgroundImage: customActive
                ? undefined
                : "linear-gradient(135deg, #ef5350 0%, #ff9600 33%, #00c853 66%, #2196f3 100%)",
            }}
            aria-hidden
          />
          Custom
        </button>
      </div>

      {customPickerOpen ? (
        <IndicatorColorPicker
          label={label}
          color={color}
          onPreview={onColorChange}
          onApply={handleCustomApply}
          onCancel={handleCustomCancel}
        />
      ) : null}

      <ToggleGroup
        type="single"
        value={lineStyle}
        onValueChange={(v) => {
          if (v === "solid" || v === "dashed" || v === "dotted") {
            onStyleChange(v);
          }
        }}
        className="w-full justify-stretch"
      >
        <ToggleGroupItem value="solid" className="flex-1 text-xs h-8">
          Solid
        </ToggleGroupItem>
        <ToggleGroupItem value="dashed" className="flex-1 text-xs h-8">
          Dashed
        </ToggleGroupItem>
        <ToggleGroupItem value="dotted" className="flex-1 text-xs h-8">
          Dotted
        </ToggleGroupItem>
      </ToggleGroup>
    </div>
  );
}
